import { Input } from '@billy/design-system'
import { ReactElement, useCallback, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { useInvoiceComplianceMode } from '@features/invoices/hooks/useInvoiceComplianceMode'
import { VatExemptionMention } from '@features/invoicingSettings/elements/VatExemptionMention'
import { useTrackInvoicingSettings } from '@features/invoicingSettings/hooks/useTrackInvoicingSettings'
import { VatRateSelect } from '@features/vatRates/elements/VatRateSelect'
import { useGetVatRates } from '@features/vatRates/hooks/useGetVatRates'
import { getLocalVatRates } from '@features/vatRates/utils/getLocalVatRates'

import { useFormContext } from '../../../../../../../../../../../../hooks'
import { useInvoiceSettingsModals } from '../../../../../../contexts/invoiceSettingsModalsContext'
import { InvoiceSettingsFormSchema } from '../../../../../../types/invoiceSettingsFormSchema'

export const DefaultVat = (): ReactElement => {
  const { t } = useTranslation()
  const { trackInvoicingSettings } = useTrackInvoicingSettings()
  const { FormItem, setValue, watch } = useFormContext<InvoiceSettingsFormSchema>()
  const { isFrenchComplianceMode } = useInvoiceComplianceMode()
  const { vatRates, isLoading } = useGetVatRates()
  const { vatExemptionMentionModal } = useInvoiceSettingsModals()
  const {
    openModal: openVatExemptionMentionModal,
    shouldUpdateForm,
    vatExemptionMentionScheme: modalVatExemptionMentionScheme,
  } = vatExemptionMentionModal

  const [vatRateId, vatExemptionMentionScheme] = watch(['vatRateId', 'legalMentions.custom.vatExemptionScheme'])

  const localVatRates = useMemo(() => getLocalVatRates(vatRates), [vatRates])
  const vatRateOptions = isFrenchComplianceMode ? localVatRates : vatRates

  const shouldShowVatExemptionMention = useMemo(() => {
    if (!isFrenchComplianceMode) {
      return false
    }
    const selectedLocalVatRate = localVatRates.find((vatRate) => vatRate.id === vatRateId)
    return selectedLocalVatRate?.rate === 0
  }, [isFrenchComplianceMode, localVatRates, vatRateId])

  useEffect(() => {
    if (isFrenchComplianceMode && shouldUpdateForm) {
      setValue('legalMentions.custom.vatExemptionScheme', modalVatExemptionMentionScheme || null, {
        shouldDirty: false,
      })
    }
    // Only re-run when modalVatExemptionMentionValue changes to avoid infinite loop with setValue
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFrenchComplianceMode, modalVatExemptionMentionScheme, shouldUpdateForm])

  const handleOpenVatExemptionMentionModal = useCallback(() => {
    openVatExemptionMentionModal(vatExemptionMentionScheme ?? '')
  }, [openVatExemptionMentionModal, vatExemptionMentionScheme])

  return (
    <>
      <FormItem
        label={t('settings.organization.external_invoice.general.default_vat_rate.label')}
        name="vatRateId"
        render={({ field: { value, onChange, ...rest } }) => {
          return (
            <VatRateSelect
              {...rest}
              dropdownFetching={isLoading}
              dropdownSize="fitTrigger"
              onSelect={(_id, vatRate) => {
                onChange(vatRate?.id)
                setValue('vatRatePercent', vatRate?.rate)
                trackInvoicingSettings('Default VAT Rate dropdown selected', { value: vatRate?.rate })
              }}
              selectLook={false}
              selectedId={value}
              vatRates={vatRateOptions}
              withSearch={false}
            />
          )
        }}
      />
      {shouldShowVatExemptionMention && (
        <VatExemptionMention
          onOpenModal={handleOpenVatExemptionMentionModal}
          vatExemptionMentionScheme={vatExemptionMentionScheme}
          isLocalVatRate
        />
      )}
      <Input name="vatRatePercent" hidden />
    </>
  )
}
